import axios from 'axios';

// Create axios instance with default config
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  withCredentials: true,
  timeout: 10000,
});

// Request interceptor for adding auth headers
api.interceptors.request.use(
  (config) => {
    // Add any default headers here if needed
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for handling errors
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      window.location.href = '/admin/placement-dashboard/login';
    }
    return Promise.reject(error);
  }
);

// Auth API functions
export const authAPI = {
  login: async (credentials) => {
    const response = await api.post('/auth/login', credentials);
    return response.data;
  },

  logout: async () => {
    const response = await api.post('/auth/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },

  checkAuth: async () => {
    const response = await api.get('/auth/check');
    return response.data;
  },

  changePassword: async (passwords) => {
    const response = await api.post('/auth/change-password', passwords);
    return response.data;
  },
};

// Placement API functions
export const placementAPI = {
  // Get all placements with filtering and pagination
  getPlacements: async (params = {}) => {
    const response = await api.get('/placements', { params });
    return response.data;
  },

  // Get single placement by ID
  getPlacement: async (id) => {
    const response = await api.get(`/placements/${id}`);
    return response.data;
  },

  // Create new placement
  createPlacement: async (placementData) => {
    const response = await api.post('/placements', placementData);
    return response.data;
  },

  // Update placement
  updatePlacement: async (id, placementData) => {
    const response = await api.put(`/placements/${id}`, placementData);
    return response.data;
  },

  // Delete placement
  deletePlacement: async (id) => {
    const response = await api.delete(`/placements/${id}`);
    return response.data;
  },

  // Bulk delete placements
  bulkDeletePlacements: async (ids) => {
    const response = await api.post('/placements/bulk-delete', { ids });
    return response.data;
  },

  // Get analytics data
  getAnalytics: async (season = null) => {
    const params = season ? { season } : {};
    const response = await api.get('/placements/analytics', { params });
    return response.data;
  },

  // Export placements data
  exportPlacements: async (format = 'csv', season = null) => {
    const params = { format };
    if (season) params.season = season;
    
    const response = await api.get('/placements/export', {
      params,
      responseType: 'blob',
    });
    
    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `placements_${Date.now()}.${format}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true };
  },
};

// Utility functions for API calls
export const apiUtils = {
  // Handle API errors consistently
  handleError: (error) => {
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      return {
        message: data.message || data.error || 'An error occurred',
        details: data.details || [],
        status,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error - please check your connection',
        details: [],
        status: 0,
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        details: [],
        status: 0,
      };
    }
  },

  // Format date for API
  formatDateForAPI: (date) => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    return date.toISOString().split('T')[0];
  },

  // Parse date from API
  parseDateFromAPI: (dateString) => {
    if (!dateString) return null;
    return new Date(dateString);
  },

  // Build query string for filters
  buildQueryString: (filters) => {
    const params = new URLSearchParams();
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          params.append(key, value.join(','));
        } else {
          params.append(key, value.toString());
        }
      }
    });
    
    return params.toString();
  },

  // Debounce function for search inputs
  debounce: (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  },
};

// Constants for the application
export const API_CONSTANTS = {
  APPLICATION_STATUSES: [
    'Applied',
    'Rejected',
    'PPO Rejected',
    'Session',
    'Interview Scheduled',
    'Offer Received',
    'Accepted',
    'Declined',
  ],
  
  SEASONS: [
    '2025 Placement',
    '2024 Intern',
    '2023 Placement',
    '2026 Placement',
  ],
  
  PRIORITY_RATINGS: [
    { value: 1, label: '1 Star' },
    { value: 2, label: '2 Stars' },
    { value: 3, label: '3 Stars' },
    { value: 4, label: '4 Stars' },
    { value: 5, label: '5 Stars' },
  ],
  
  STATUS_COLORS: {
    'Applied': '#007bff',
    'Rejected': '#dc3545',
    'PPO Rejected': '#fd7e14',
    'Session': '#6f42c1',
    'Interview Scheduled': '#ffc107',
    'Offer Received': '#28a745',
    'Accepted': '#20c997',
    'Declined': '#6c757d',
  },
  
  EXPORT_FORMATS: [
    { value: 'csv', label: 'CSV' },
    { value: 'json', label: 'JSON' },
  ],
};

export default api;
